import { z } from "zod";

const identifierSchema = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .regex(/^[a-zA-Z0-9_.:-]+$/u);
const dateTimeSchema = z.string().datetime({ offset: true });
const nullableDateTimeSchema = dateTimeSchema.nullable();
const jsonObjectSchema = z.record(z.string(), z.unknown());

const mediaKindSchema = z.enum(["image", "video"]);
const mediaOperationSchema = z.enum(["generate", "edit", "image_to_video"]);
const mediaRoleSchema = z.enum(["master", "derivative", "output"]);
export const creativeMediaStatusSchema = z.enum([
  "draft", "queued", "generating", "validating", "uploading", "succeeded", "failed", "canceled",
]);

export const creativeMediaJobReferenceDataSchema = z.object({
  schema: z.literal("conversation_creative_media_job.v1"),
  job_id: identifierSchema,
  origin_turn_id: identifierSchema,
  media_kind: mediaKindSchema,
  operation: mediaOperationSchema,
}).strict();

export function collectCreativeMediaJobReferences(input: unknown) {
  if (!Array.isArray(input)) return [];
  return input.flatMap((value: unknown) => {
    if (!value || typeof value !== "object" || !("type" in value) ||
        value.type !== "data-creative-media-job" || !("data" in value)) return [];
    const parsed = creativeMediaJobReferenceDataSchema.safeParse(value.data);
    return parsed.success ? [parsed.data] : [];
  });
}

export const conversationCreativeMediaV2DataSchema = z
  .object({
    candidate_id: identifierSchema,
    checksum: z.string().max(300),
    job_id: identifierSchema,
    media_kind: mediaKindSchema,
    mime_type: z.string().max(100),
    operation: mediaOperationSchema,
    ordinal: z.number().int().nonnegative(),
    presentation: z.enum(["single", "mosaic"]).optional(),
    resource_id: identifierSchema,
    role: mediaRoleSchema,
    schema: z.literal("conversation_creative_media.v2"),
    size_bytes: z.number().int().nonnegative(),
    version: z.string().max(300),
  })
  .strict();

const conversationCreativeMediaLegacyDataSchema = z
  .object({
    checksum: z.string().max(300).optional().default(""),
    media_kind: mediaKindSchema,
    mime_type: z.string().max(100).optional().default(""),
    ordinal: z.number().int().nonnegative().optional().default(0),
    resource_id: identifierSchema,
    role: mediaRoleSchema,
    size_bytes: z.number().int().nonnegative().optional().default(0),
    version: z.string().max(300).optional().default(""),
  })
  .passthrough();

export type ConversationCreativeMediaPart = {
  data: z.infer<typeof conversationCreativeMediaV2DataSchema>;
  type: "data-creative-media";
};

const progressErrorSchema = z.union([
  z.object({ code: z.literal("media_generation_failed"), message: z.literal("媒体生成失败，请查看任务状态。") }).strict(),
  z.object({ code: z.literal("media_provider_result_ambiguous"), message: z.literal("生成结果暂无法确认，请勿重复提交。") }).strict(),
  z.object({ code: z.literal("media_canceled"), message: z.literal("媒体任务已取消。") }).strict(),
]);
export const creativeMediaProgressJobSchema = z.object({
  actor_user_id: identifierSchema,
  canceled_at: nullableDateTimeSchema,
  completed_at: nullableDateTimeSchema,
  conversation_id: identifierSchema,
  created_at: dateTimeSchema,
  error: progressErrorSchema.nullable(),
  job_id: identifierSchema,
  media_kind: mediaKindSchema,
  operation: mediaOperationSchema,
  organization_id: identifierSchema,
  origin_turn_id: identifierSchema.nullable(),
  resource_parts: z.array(z.object({
    type: z.literal("data-creative-media"), data: conversationCreativeMediaV2DataSchema,
  }).strict()),
  revision: z.number().int().positive(),
  started_at: nullableDateTimeSchema,
  status: creativeMediaStatusSchema,
}).strict().superRefine((job, context) => {
  const created = Date.parse(job.created_at);
  const terminal = ["succeeded", "failed", "canceled"].includes(job.status);
  const invalidTime = terminal !== (job.completed_at !== null) ||
    (job.started_at !== null && Date.parse(job.started_at) < created) ||
    (job.completed_at !== null && Date.parse(job.completed_at) < created) ||
    (job.started_at !== null && job.completed_at !== null && Date.parse(job.completed_at) < Date.parse(job.started_at)) ||
    (job.canceled_at !== null && (job.status !== "canceled" || Date.parse(job.canceled_at) < created));
  const invalidResources = (job.status !== "succeeded" && job.resource_parts.length > 0) ||
    job.resource_parts.some((part) => part.data.job_id !== job.job_id || part.data.media_kind !== job.media_kind || part.data.operation !== job.operation);
  const invalidError = job.status === "failed" ? job.error !== null && job.error.code === "media_canceled" :
    job.status === "canceled" ? job.error !== null && job.error.code !== "media_canceled" : job.error !== null;
  if (invalidTime || invalidResources || invalidError) {
    context.addIssue({ code: "custom", message: "媒体进度时间、状态或资源归属不符合合同" });
  }
});

export const creativeMediaProgressResponseSchema = z.object({
  schema: z.literal("creative_media_progress.v1"),
  server_now: dateTimeSchema,
  jobs: z.array(creativeMediaProgressJobSchema).max(100),
  has_more: z.boolean(),
  next_cursor: z.string().min(1).max(4000).nullable(),
}).strict().superRefine((page, context) => {
  if (page.has_more !== (page.next_cursor !== null) || page.jobs.some((job) =>
    Date.parse(job.created_at) > Date.parse(page.server_now) ||
    (job.started_at !== null && Date.parse(job.started_at) > Date.parse(page.server_now)) ||
    (job.canceled_at !== null && Date.parse(job.canceled_at) > Date.parse(page.server_now)) ||
    (job.completed_at !== null && Date.parse(job.completed_at) > Date.parse(page.server_now)))) {
    context.addIssue({ code: "custom", message: "媒体进度分页或服务端时间不符合合同" });
  }
});

export type CreativeMediaProgressJob = z.infer<typeof creativeMediaProgressJobSchema>;
export type CreativeMediaJobReference = z.infer<typeof creativeMediaJobReferenceDataSchema>;

export function projectConversationCreativeMediaPart(
  input: unknown,
  legacyGroupId: string,
): ConversationCreativeMediaPart | null {
  if (!input || typeof input !== "object" || Array.isArray(input)) return null;
  const part = input as Record<string, unknown>;
  if (part.type !== "data-creative-media") return null;
  const v2 = conversationCreativeMediaV2DataSchema.safeParse(part.data);
  if (v2.success) return { data: v2.data, type: "data-creative-media" };
  if (
    part.data &&
    typeof part.data === "object" &&
    !Array.isArray(part.data) &&
    (part.data as Record<string, unknown>).schema ===
      "conversation_creative_media.v2"
  ) {
    return null;
  }

  const legacy = conversationCreativeMediaLegacyDataSchema.safeParse(part.data);
  if (!legacy.success) return null;
  const jobId = `legacy:${legacyGroupId}`;
  return {
    data: {
      candidate_id: `${jobId}:${legacy.data.role}:${legacy.data.ordinal}`,
      checksum: legacy.data.checksum,
      job_id: jobId,
      media_kind: legacy.data.media_kind,
      mime_type: legacy.data.mime_type,
      operation: "generate",
      ordinal: legacy.data.ordinal,
      presentation: "single",
      resource_id: legacy.data.resource_id,
      role: legacy.data.role,
      schema: "conversation_creative_media.v2",
      size_bytes: legacy.data.size_bytes,
      version: legacy.data.version,
    },
    type: "data-creative-media",
  };
}

const creativeMediaJobWireSchema = z
  .object({
    actor_user_id: identifierSchema,
    attempt: z.number().int().nonnegative(),
    brief_id: identifierSchema,
    canceled_at: nullableDateTimeSchema,
    completed_at: nullableDateTimeSchema,
    conversation_id: identifierSchema,
    created_at: dateTimeSchema,
    error_summary: jsonObjectSchema,
    execution_kind: z.string().min(1).max(80),
    id: identifierSchema,
    max_attempts: z.number().int().positive(),
    media_kind: mediaKindSchema,
    model: z.string().max(160),
    next_attempt_at: nullableDateTimeSchema,
    observed_summary: jsonObjectSchema,
    operation: mediaOperationSchema,
    organization_id: identifierSchema,
    parent_job_id: z.preprocess(
      (value) => value === "" ? null : value,
      identifierSchema.nullable(),
    ),
    provider: z.string().max(80),
    request_summary: jsonObjectSchema,
    result_summary: jsonObjectSchema,
    revision: z.number().int().positive(),
    started_at: nullableDateTimeSchema,
    status: z.enum([
      "draft",
      "queued",
      "generating",
      "validating",
      "uploading",
      "succeeded",
      "failed",
      "canceled",
    ]),
    target_agent: z.string().min(1).max(100),
    updated_at: dateTimeSchema,
  })
  .strict();

const creativeMediaResourceWireSchema = z
  .object({
    checksum: z.string().max(300),
    created_at: dateTimeSchema,
    id: identifierSchema,
    job_id: identifierSchema,
    metadata_summary: jsonObjectSchema,
    mime_type: z.string().max(100),
    ordinal: z.number().int().nonnegative(),
    resource_id: identifierSchema,
    role: z.string().min(1).max(80),
    size_bytes: z.number().int().nonnegative(),
    version: z.string().max(300),
  })
  .strict();

const creativeMediaSelectionWireSchema = z
  .object({
    actor_user_id: identifierSchema,
    conversation_id: identifierSchema,
    created_at: dateTimeSchema,
    id: identifierSchema,
    job_id: identifierSchema,
    organization_id: identifierSchema,
    resource_id: identifierSchema,
    revision: z.number().int().positive(),
    status: z.enum(["accepted", "revoked"]),
    updated_at: dateTimeSchema,
  })
  .strict();

function toSelection(input: z.infer<typeof creativeMediaSelectionWireSchema>) {
  return {
    actorUserId: input.actor_user_id,
    conversationId: input.conversation_id,
    createdAt: input.created_at,
    id: input.id,
    jobId: input.job_id,
    organizationId: input.organization_id,
    resourceId: input.resource_id,
    revision: input.revision,
    status: input.status,
    updatedAt: input.updated_at,
  };
}

export const creativeMediaJobResponseSchema = z
  .object({
    job: creativeMediaJobWireSchema,
    resources: z.array(creativeMediaResourceWireSchema),
    selection: creativeMediaSelectionWireSchema.nullable(),
  })
  .strict()
  .transform((response) => ({
    job: {
      actorUserId: response.job.actor_user_id,
      conversationId: response.job.conversation_id,
      id: response.job.id,
      mediaKind: response.job.media_kind,
      operation: response.job.operation,
      organizationId: response.job.organization_id,
      parentJobId: response.job.parent_job_id,
      revision: response.job.revision,
      status: response.job.status,
    },
    resources: response.resources.map((resource) => ({
      checksum: resource.checksum,
      jobId: resource.job_id,
      metadataSummary: resource.metadata_summary,
      mimeType: resource.mime_type,
      ordinal: resource.ordinal,
      resourceId: resource.resource_id,
      role: resource.role,
      sizeBytes: resource.size_bytes,
      version: resource.version,
    })),
    selection: response.selection ? toSelection(response.selection) : null,
  }));

export const creativeMediaSelectionResponseSchema = z
  .object({ selection: creativeMediaSelectionWireSchema.nullable() })
  .strict()
  .transform((response) => ({
    selection: response.selection ? toSelection(response.selection) : null,
  }));

export type CreativeMediaJob = ReturnType<
  typeof creativeMediaJobResponseSchema.parse
>["job"];
export type CreativeMediaResource = ReturnType<
  typeof creativeMediaJobResponseSchema.parse
>["resources"][number];
export type CreativeMediaSelection = NonNullable<
  ReturnType<typeof creativeMediaSelectionResponseSchema.parse>["selection"]
>;
